import React, { useCallback, useLayoutEffect, useMemo, } from 'react';
import { Platform, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { useAppSelector } from 'store/hooks';
import { AppView, Button, ButtonGroup, rem, SCROLL_INDICATOR_INSETS, SettingsItem, withAnimatedNavigationHeader, } from 'design-system';
import { HEIGHT } from 'design-system/Button';
import CardHeader from 'design-system/CardHeader';
import { IconCoinStack, IconHeartBeat, IconHome, IconLock, } from 'design-system/icons';
import IconEyes from 'design-system/icons/IconEyes';
import IconGraph from 'design-system/icons/IconGraph';
import IconInvest from 'design-system/icons/IconInvest';
import IconPercent from 'design-system/icons/IconPercent';
import IconRectangleHistory from 'design-system/icons/IconRectangleHistory';
import IconThreeUser from 'design-system/icons/IconThreeUser';
import IconTransfer from 'design-system/icons/IconTransfer';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import StatusBar from 'design-system/StatusBar';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { selectMaximumInterest, selectUserTierMaxPotInterest, } from 'features/pots/reducers/selectors';
import { tierThemes } from 'features/premium/benefits';
import TierBenefit from 'features/premium/components/TierBenefit';
import TierFlatListFooter from 'features/premium/components/TierFlatListFooter';
import { isPremiumTier, Tier } from 'features/premium/entities';
import { selectConnectionsLimit, selectFilteredBenefitsDataForTierFlattened, } from 'features/premium/selectors';
import { selectUserTier } from 'features/premium/selectors/tier';
import createStyleSheets from 'utils/createStyleSheets';
const strings = {
    deleteAnyway: 'Delete anyway',
    takeMeBack: 'Take me back',
};
const homeCountryMap = {
    GB: 'the UK',
    US: 'the US',
    CA: 'Canada',
};
const DeleteBenefitsScreen = withAnimatedNavigationHeader(({ navigation, scrollHandler }) => {
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
        });
    }, []);
    const guessedHomeCountry = useAppSelector((store) => store.user.user.guessedHomeCountry);
    const maxInterestRate = useAppSelector(selectMaximumInterest);
    const userTierMaxInterest = useAppSelector(selectUserTierMaxPotInterest);
    const connectionsLimitMap = useAppSelector(selectConnectionsLimit);
    const tier = useAppSelector(selectUserTier);
    const filteredBenefitsData = useAppSelector((store) => selectFilteredBenefitsDataForTierFlattened(store, tier));
    const benefits = useMemo(() => {
        const allBenefits = {
            bankLogins: {
                title: `Link up to ${connectionsLimitMap?.FREE} bank connections`,
                description: `Connect more than 30+ banks in ${homeCountryMap[guessedHomeCountry] || guessedHomeCountry} to see where your money goes.`,
                icon: <IconCoinStack gradientKey="yellow"/>,
                type: 'productBenefit',
            },
            trackAndBudget: {
                title: 'Track and budget your money',
                description: 'Emma automatically tracks and categorises your expenses across accounts.',
                icon: <IconGraph />,
                type: 'productBenefit',
            },
            creditHistory: {
                title: 'Improve your credit history',
                description: 'Report your rent payments to Experian and Equifax to boost your credit history.',
                icon: <IconRectangleHistory gradientKey="lightPurple"/>,
                type: 'productBenefit',
            },
            dailyInterest: {
                title: 'Get paid daily interest',
                description: `Don’t wait 30 days. Earn up to ${maxInterestRate}% AER paid daily. FSCS protected.`,
                icon: <IconPercent gradientKey="ultimate"/>,
                type: 'productBenefit',
            },
            dailyInterestAlt: {
                title: `Earn ${userTierMaxInterest}% AER`,
                description: `Interest is paid daily and it’s protected up to £120,000 per bank by the FSCS.`,
                icon: <IconPercent gradientKey="skyBlue"/>,
                type: 'productBenefit',
            },
            investCommissionFree: {
                title: `Invest commission free`,
                description: `Invest as little as £1 in the companies you love. Capital at risk, other charges apply.`,
                icon: <IconInvest gradientKey="darkPurple"/>,
                type: 'productBenefit',
            },
            cashback: {
                title: 'You can earn up to 30% on cashback deals',
                description: 'Discover more than 500+ deals on Emma that will make you save £100s.',
                icon: <IconCoinStack gradientKey="yellow"/>,
                type: 'productBenefit',
            },
            recurringPayments: {
                title: 'Track your recurring payments',
                description: 'Never ever lose money on wasteful subscriptions. Emma also tells you when the prices go up.',
                icon: <IconHome gradientKey="skyBlue"/>,
                type: 'productBenefit',
            },
            alwaysKnow: {
                title: 'Always know where you spend your money',
                description: 'Connect all your accounts in one places to track where you are spending the most.',
                icon: <IconEyes gradientKey="ultimate"/>,
                type: 'productBenefit',
            },
            iconFraud: {
                title: 'Protect your identity on the dark web',
                description: 'Protect your online identity and find if your personal data has been leaked on the dark web.',
                icon: <IconLock alt/>,
                type: 'productBenefit',
            },
            financialHealth: {
                title: 'Track your financial health',
                description: 'Emma is able to track your net worth and help you improve it over time.',
                icon: <IconHeartBeat gradientKey="apricot"/>,
                type: 'productBenefit',
            },
            emmaGroups: {
                title: 'Share your spending with Emma Groups',
                description: 'Keep track of your shared spending and balances with housemates, trips, groups, friends, and family.',
                icon: <IconThreeUser gradientKey="darkPurple"/>,
                type: 'productBenefit',
            },
            payAnyoneInstantly: {
                title: 'Pay anyone instantly',
                description: 'Send and request money by sending payment links or flashing QR codes.',
                icon: <IconTransfer gradientKey="lightPink"/>,
                type: 'productBenefit',
            },
            seeWhereYourMoneyGoes: {
                title: 'See where your money goes',
                description: 'Emma has some of the most powerful analytics in finance that will help you achieve your financial goals.',
                icon: <IconEyes gradientKey="lightPink"/>,
                type: 'productBenefit',
            },
            takeControlOfSubscriptions: {
                title: 'Take control of subscriptions',
                description: 'Find and cancel wasteful subscriptions in Emma.',
                icon: <IconHome gradientKey="lightPink"/>,
                type: 'productBenefit',
            },
        };
        if (isPremiumTier(tier)) {
            return filteredBenefitsData.map((item) => ({
                ...item,
                type: 'tierBenefit',
            }));
        }
        if (guessedHomeCountry === 'GB') {
            return [
                allBenefits.bankLogins,
                allBenefits.trackAndBudget,
                allBenefits.dailyInterestAlt,
                allBenefits.investCommissionFree,
                allBenefits.payAnyoneInstantly,
            ];
        }
        return [
            allBenefits.bankLogins,
            allBenefits.trackAndBudget,
            allBenefits.seeWhereYourMoneyGoes,
            allBenefits.takeControlOfSubscriptions,
            allBenefits.emmaGroups,
        ];
    }, [
        connectionsLimitMap?.FREE,
        filteredBenefitsData,
        guessedHomeCountry,
        maxInterestRate,
        tier,
        userTierMaxInterest,
    ]);
    const onPressDelete = useCallback(() => {
        if (tier === Tier.free) {
            navigation.navigateDeprecated('DeleteTrust');
        }
        else {
            navigation.navigateDeprecated('DeleteSupport');
        }
    }, [tier]);
    const onPressBack = useCallback(() => {
        navigation.popToTop();
    }, []);
    const premiumBenefits = tier !== Tier.free;
    const styles = useStyles(styleSet);
    const renderItem = useCallback(({ item, index }) => (<View style={[
            styles.rowBenefit,
            index === benefits.length - 1 && styles.itemContainerLast,
            !premiumBenefits && index === 0 && styles.itemContainerFirst,
        ]}>
          {item.type === 'tierBenefit' ? (<TierBenefit item={item} customName={item.customData?.customName} customDescription={item.customData?.customDescription} customIcon={item.customData?.customIcon}/>) : (<SettingsItem {...item}/>)}
        </View>), [
        benefits.length,
        premiumBenefits,
        styles.itemContainerFirst,
        styles.itemContainerLast,
        styles.rowBenefit,
    ]);
    const infoHeader = useMemo(() => {
        const tooExpensiveTitle = tier === Tier.free
            ? {
                title: 'You are on a basic plan',
                subTitle: 'Discover all our free features you can leverage to take control of your money',
            }
            : {
                title: 'Are you sure about this?',
                subTitle: `These are all the benefits you are going to lose on your ${tierThemes[tier].name} plan`,
            };
        const title = tooExpensiveTitle;
        return (<ScreenInfoHeader title={title.title} subTitle={title.subTitle} smallSubTitle/>);
    }, [tier]);
    const renderHeader = useCallback(() => (<>
          {infoHeader}
          {premiumBenefits ? (<View style={[styles.rowBenefit, styles.itemContainerFirst]}>
              <CardHeader>You will lose:</CardHeader>
            </View>) : null}
        </>), [
        infoHeader,
        premiumBenefits,
        styles.itemContainerFirst,
        styles.rowBenefit,
    ]);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT * 2 + rem(24), paddingHorizontalStyle);
    const renderFooter = useMemo(() => <TierFlatListFooter />, []);
    const tooExpensiveBackButton = tier === Tier.free ? 'Explore features' : 'No, take me back';
    return (<AppView withFrame={false}>
        <Animated.FlatList onScroll={scrollHandler} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={contentContainerStyle} ListFooterComponent={renderFooter} data={benefits} ListHeaderComponent={renderHeader} renderItem={renderItem}/>
        <StatusBar barStyle="dark-content" animated/>
        <ButtonGroup type="floating" margins>
          <Button title={premiumBenefits ? 'I am sure' : strings.deleteAnyway} brandReversed onPress={onPressDelete} testID="deleteButton"/>
          <Button title={tooExpensiveBackButton} brand onPress={onPressBack} testID="backButton"/>
        </ButtonGroup>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styleSet = createStyleSheets((colors) => ({
    rowBenefit: {
        marginHorizontal: rem(16),
        backgroundColor: colors.cards.onDark,
    },
    itemContainerFirst: {
        borderTopLeftRadius: rem(16),
        borderTopRightRadius: rem(16),
        ...(Platform.OS === 'ios' && {
            overflow: 'hidden',
        }),
    },
    itemContainerLast: {
        borderBottomLeftRadius: rem(16),
        borderBottomRightRadius: rem(16),
        ...(Platform.OS === 'ios' && {
            overflow: 'hidden',
        }),
    },
}));
export default DeleteBenefitsScreen;
